import React, { useState, useMemo, useCallback } from 'react';
import { useApiMode } from '../context/ApiModeContext';
import './ApiExplorer.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const API_TABS = [
  { id: 'create', label: 'Create' },
  { id: 'edit', label: 'Edit' },
  { id: 'get-all', label: 'Get All' },
  { id: 'get', label: 'Get' },
];

const getDefaultCreatePayload = () => ({
  beneficialOwnerShip: {
    hasAttestation: true,
    individualOwnerShip: [
      {
        firstName: 'John',
        lastName: 'Duenas',
        ownershipPercentage: 25,
        dateOfBirth: '12/31/1990',
        citizenshipIsoCountryCode: 'US',
        addressLine1: 'Address line 1',
        addressLine2: 'Address line 2',
        addressCity: 'San Francisco',
        addressState: 'CA',
        addressPostalCode: '94111',
        addressIsoCountryCode: 'US',
        phoneNumberType: 'mobile',
        dialCode: '+1',
        phoneNumber: '4152306359',
        taxId: '121-31-2321',
        isControlPerson: true,
        title: 'CEO of the company',
      },
    ],
    businessOwnerShip: [
      {
        businessName: 'Another business name',
        taxId: '33-1867308',
        ownershipPercentage: 25,
        addressLine1: 'Address line 1',
        addressLine2: 'Address line 2',
        addressCity: 'San Francisco',
        addressState: 'CA',
        addressPostalCode: '94111',
        addressIsoCountryCode: 'US',
      },
    ],
  },
  onBehalfAccountId: 412312,
});

const getDefaultEditPayload = () => {
  const create = getDefaultCreatePayload();
  return { ...create, id: 1 };
};

const sampleResponse = {
  beneficialOwnerShip: {
    individualOwnerShip: [
      {
        id: 1,
        firstName: 'John',
        lastName: 'Duenas',
        ownershipPercentage: 25,
        dateOfBirth: '12/31/1990',
        citizenshipIsoCountryCode: 'US',
        addressLine1: 'Address line 1',
        addressLine2: 'Address line 2',
        addressCity: 'San Francisco',
        addressState: 'CA',
        addressPostalCode: '94111',
        addressIsoCountryCode: 'US',
      },
    ],
    businessOwnerShip: [
      {
        id: 2,
        businessName: 'Another business name',
        taxId: '33-1867308',
        ownershipPercentage: 25,
        addressLine1: 'Address line 1',
        addressCity: 'San Francisco',
        addressState: 'CA',
        addressPostalCode: '94111',
        addressIsoCountryCode: 'US',
      },
    ],
  },
};

const BeneficiaryApiExplorer = ({
  baseUrl: baseUrlProp,
  partners = [],
  onClose,
  onLogEntry,
}) => {
  const { apiBaseUrl: contextBaseUrl } = useApiMode();
  const baseUrl = baseUrlProp || contextBaseUrl;
  const [activeTab, setActiveTab] = useState('create');
  const [createJson, setCreateJson] = useState(() =>
    JSON.stringify(getDefaultCreatePayload(), null, 2)
  );
  const [editJson, setEditJson] = useState(() =>
    JSON.stringify(getDefaultEditPayload(), null, 2)
  );
  const [getAllAccountId, setGetAllAccountId] = useState('412312');
  const [getBeneficiaryId, setGetBeneficiaryId] = useState('1');
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const base = baseUrl.replace(/\/$/, '');

  const editIdFromJson = useMemo(() => {
    try {
      const o = JSON.parse(editJson);
      return o.id != null ? String(o.id) : '1';
    } catch {
      return '1';
    }
  }, [editJson]);

  const tabConfig = useMemo(() => ({
    create: {
      method: 'POST',
      endpoint: `${base}/api/v1/beneficiaries`,
      fullUrl: `${base}/api/v1/beneficiaries`,
    },
    edit: {
      method: 'PUT',
      endpoint: `${base}/api/v1/beneficiaries/{id}`,
      fullUrl: `${base}/api/v1/beneficiaries/${editIdFromJson}`,
    },
    'get-all': {
      method: 'GET',
      endpoint: `${base}/api/v1/beneficiaries?onBehalfAccountId={onBehalfAccountId}`,
      fullUrl: `${base}/api/v1/beneficiaries?onBehalfAccountId=${getAllAccountId}`,
    },
    get: {
      method: 'GET',
      endpoint: `${base}/api/v1/beneficiaries/{id}`,
      fullUrl: `${base}/api/v1/beneficiaries/${getBeneficiaryId}`,
    },
  }), [base, editIdFromJson, getAllAccountId, getBeneficiaryId]);

  const currentFullUrl = tabConfig[activeTab]?.fullUrl || '';

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleSend = async () => {
    setError('');
    setIsLoading(true);
    const partnerName = partners[0] ? partners[0].name : 'Connection';
    try {
      const base = baseUrl.replace(/\/$/, '');
      let url; let method; let body;
      if (activeTab === 'create') {
        url = `${base}/api/v1/beneficiaries`;
        method = 'POST';
        body = JSON.stringify(JSON.parse(createJson));
      } else if (activeTab === 'edit') {
        url = `${base}/api/v1/beneficiaries/${editIdFromJson}`;
        method = 'PUT';
        body = JSON.stringify(JSON.parse(editJson));
      } else if (activeTab === 'get-all') {
        url = `${base}/api/v1/beneficiaries?onBehalfAccountId=${getAllAccountId}`;
        method = 'GET';
      } else {
        url = `${base}/api/v1/beneficiaries/${getBeneficiaryId}`;
        method = 'GET';
      }
      const opts = method === 'GET' ? {} : { headers: { 'Content-Type': 'application/json' }, body };
      const r = await fetch(url, { method, ...opts });
      const res = await r.json().catch(() => ({}));
      const responseStr = JSON.stringify(res, null, 2);
      setResponseText(responseStr);
      const actionLabel = activeTab === 'create' ? 'Create Beneficiary API' : activeTab === 'edit' ? 'Edit Beneficiary API' : activeTab === 'get-all' ? 'Get All Beneficiaries API' : 'Get Beneficiary API';
      if (onLogEntry && partnerName) {
        onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: responseStr.slice(0, 80) + '...', amount: 0 });
      }
    } catch (e) {
      setError(e.message || 'Request failed');
      setResponseText('');
      if (onLogEntry && partnerName) {
        onLogEntry(partnerName, { action: 'Beneficiary API', status: 'Failed', details: String(e.message), amount: 0 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (activeTab === 'create') {
      setCreateJson(JSON.stringify(getDefaultCreatePayload(), null, 2));
    } else if (activeTab === 'edit') {
      setEditJson(JSON.stringify(getDefaultEditPayload(), null, 2));
    }
    setResponseText('');
    setError('');
  };

  const requestSections = {
    create: {
      title: 'Request body',
      content: (
        <textarea
          className="api-textarea"
          value={createJson}
          onChange={(e) => setCreateJson(e.target.value)}
          rows={14}
          placeholder='{ "beneficialOwnerShip": { ... }, "onBehalfAccountId": 412312 }'
        />
      ),
      showActions: true,
    },
    edit: {
      title: 'Request body',
      content: (
        <textarea
          className="api-textarea"
          value={editJson}
          onChange={(e) => setEditJson(e.target.value)}
          rows={14}
          placeholder='{ "id": 1, "beneficialOwnerShip": { ... }, "onBehalfAccountId": 412312 }'
        />
      ),
      showActions: true,
    },
    'get-all': {
      title: 'Query parameters',
      content: (
        <div className="api-form">
          <div className="form-group">
            <label>onBehalfAccountId (required)</label>
            <input
              type="text"
              className="form-input"
              value={getAllAccountId}
              onChange={(e) => setGetAllAccountId(e.target.value)}
              placeholder="412312"
            />
          </div>
        </div>
      ),
      showActions: true,
    },
    get: {
      title: 'Path parameter',
      content: (
        <div className="api-form">
          <div className="form-group">
            <label>id</label>
            <input
              type="text"
              className="form-input"
              value={getBeneficiaryId}
              onChange={(e) => setGetBeneficiaryId(e.target.value)}
              placeholder="1"
            />
          </div>
        </div>
      ),
      showActions: true,
    },
  };

  const responseSections = {
    create: JSON.stringify({ beneficialOwnerShip: sampleResponse.beneficialOwnerShip }, null, 2),
    edit: JSON.stringify({ beneficialOwnerShip: sampleResponse.beneficialOwnerShip }, null, 2),
    'get-all': JSON.stringify({ beneficialOwnerShip: sampleResponse.beneficialOwnerShip }, null, 2),
    get: JSON.stringify(sampleResponse, null, 2),
  };

  const errorSections = {
    create: [
      { code: '400', description: 'Bad Request – Invalid or missing required fields' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '422', description: 'Unprocessable Entity – Validation failed' },
      { code: '500', description: 'Internal Server Error' },
    ],
    edit: [
      { code: '400', description: 'Bad Request – Invalid request body' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '404', description: 'Not Found – Beneficiary not found' },
      { code: '422', description: 'Unprocessable Entity – Validation failed' },
      { code: '500', description: 'Internal Server Error' },
    ],
    'get-all': [
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '403', description: 'Forbidden – No access to this account' },
      { code: '500', description: 'Internal Server Error' },
    ],
    get: [
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '404', description: 'Not Found – Beneficiary not found' },
      { code: '500', description: 'Internal Server Error' },
    ],
  };

  const reqSection = requestSections[activeTab];
  const resSection = responseSections[activeTab];
  const errSection = errorSections[activeTab];

  return (
    <div className="api-explorer api-explorer-sidebar">
      <div className="api-explorer-header">
        <h3>API Explorer – Beneficiaries</h3>
        <button type="button" className="api-explorer-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="api-explorer-tabs">
        {API_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`api-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="api-explorer-panel">
        <div className="api-endpoint-block">
          <label className="api-endpoint-label">Base API endpoint</label>
          <div className="api-endpoint-value">{baseUrl}</div>
          <label className="api-endpoint-label">API endpoint</label>
          <div className="api-endpoint-row">
            <input type="text" className="api-endpoint-input" value={currentFullUrl} readOnly />
            <button
              type="button"
              className="api-endpoint-copy"
              onClick={() => copyToClipboard(currentFullUrl)}
              title="Copy endpoint"
              aria-label="Copy endpoint"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="api-method-badge-inline">
            <span className="api-method-badge">{tabConfig[activeTab]?.method || 'GET'}</span>
          </div>
        </div>

        {reqSection && (
          <section className="api-doc-section">
            <h4>{reqSection.title}</h4>
            {reqSection.content}
            {reqSection.showActions && (
              <div className="api-tab-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSend} disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              </div>
            )}
          </section>
        )}

        <section className="api-doc-section">
          <h4>Response</h4>
          <pre className="api-pre api-response-pre">{resSection}</pre>
        </section>

        <section className="api-doc-section">
          <h4>Error</h4>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {errSection.map((row) => (
                <tr key={row.code}>
                  <td><code>{row.code}</code></td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {reqSection?.showActions && (
        <div className="api-response-section">
          <h4>Live response</h4>
          {error && <div className="error-message">{error}</div>}
          <textarea
            className="api-textarea response-textarea"
            readOnly
            value={responseText || (isLoading ? 'Loading...' : 'Click Send to see response')}
            rows={6}
          />
        </div>
      )}
    </div>
  );
};

export default BeneficiaryApiExplorer;
